import { Crop, PixelCrop } from 'react-image-crop';

import { convertCropToPercentCrop } from '../../service/ImageHelpers/ImageHelpers';
import { Size } from '../../typings/common';
import { CreativeImageCropData, CreativeImageData } from '../common/typings';
import {
    convertCropToPixelCrop,
    getCroppedImageBlob,
    getFullImageCrop,
    getScaledImageCrop
} from '../CreativeImageCropHelper/CreativeImageCropHelper';

import { CreativeImageCropValue } from './typings';
import { SmartCenterRequestData } from './typings';

export const FULL_IMAGE_PERCENT_CROP = {
    x: 0,
    y: 0,
    width: 100,
    height: 100
};

const SMART_CENTER_CROP_ID = 0;

// max available image size (limit from backend)
const MAX_SMART_CENTER_IMAGE_WIDTH = 1500;
const MAX_SMART_CENTER_IMAGE_HEIGHT = 800;

export const getAdaptiveImageCropData = (
    naturalImageSize: Size,
    cropValue?: CreativeImageCropValue
): CreativeImageCropData => {
    if (!cropValue) {
        return {
            crop: FULL_IMAGE_PERCENT_CROP
        };
    }

    return {
        crop: convertCropToPercentCrop(naturalImageSize, cropValue.crop)
    };
};

export const getAdaptiveImageCropValue = (
    naturalImageSize: Size,
    imageData: CreativeImageData,
    cropValue?: CreativeImageCropValue
): CreativeImageCropValue => {
    if (!cropValue) {
        return {
            crop: getFullImageCrop(naturalImageSize)
        };
    }

    if (!imageData.realImageSize) {
        return cropValue;
    }

    return {
        crop: getScaledImageCrop(naturalImageSize, imageData.realImageSize, cropValue.crop)
    };
};

export const getImageCropShrinkRatio = (imageSize: Size): number => {
    const kx = Math.min(1, MAX_SMART_CENTER_IMAGE_WIDTH / imageSize.width),
        ky = Math.min(1, MAX_SMART_CENTER_IMAGE_HEIGHT / imageSize.height);

    return Math.min(kx, ky);
};

export const constructSmartCenterRequestData = (
    crop: Crop,
    imageSize: Size,
    image: HTMLImageElement
): Promise<SmartCenterRequestData> => {
    const pixelCrop = convertCropToPixelCrop(crop, imageSize);
    const fullImageCrop = getFullImageCrop(imageSize);
    const shrinkRatio = getImageCropShrinkRatio(imageSize);

    const smartCenters = [
        {
            x: pixelCrop.x * shrinkRatio,
            y: pixelCrop.y * shrinkRatio,
            width: pixelCrop.width * shrinkRatio,
            height: pixelCrop.height * shrinkRatio,
            id: SMART_CENTER_CROP_ID
        }
    ];

    const crops = [
        {
            ...fullImageCrop,
            id: SMART_CENTER_CROP_ID
        }
    ];

    return getCroppedImageBlob(fullImageCrop, image, {
        // отправлять на бек нужно уменьшенный блоб изображения
        width: fullImageCrop.width * shrinkRatio,
        height: fullImageCrop.height * shrinkRatio
    }).then((blob: Blob) => {
        return {
            blob,
            smartCenters,
            crops
        };
    });
};

export const getRealImageCrop = (imageSize: Size, crop: PixelCrop): PixelCrop => {
    const shrinkRatio = getImageCropShrinkRatio(imageSize);

    return {
        x: crop.x / shrinkRatio,
        y: crop.y / shrinkRatio,
        width: crop.width / shrinkRatio,
        height: crop.height / shrinkRatio
    };
};
